/**
 * Brilinton Product Gallery Controller — R44-265
 *
 * The single runtime owner for the main PDP gallery lifecycle:
 * - Dawn/native slider refresh
 * - large-desktop carousel_peek Flickity creation/destruction
 * - active-media state
 * - product-gallery progress and counters
 * - stable media-wrapper placement
 * - explicit transactions while variants/device contexts project gallery data
 *
 * Product-family modules may project images into the Liquid-rendered slots, but
 * they must call beginContentUpdate/endContentUpdate around any gallery change.
 */
(() => {
  'use strict';

  if (window.BrilintonProductGallery?.version === 'R44-265') {
    window.BrilintonProductGallery.boot?.(document);
    return;
  }

  const DESKTOP_FLICKITY_MIN = 1200;
  const MOBILE_LAYOUT_MAX = 989;
  const RESIZE_DEBOUNCE_MS = 160;
  const registry = new WeakMap();
  const controllers = new Set();
  let resizeTimer = 0;

  const asGallery = (value) => {
    if (!value) return null;
    if (value.matches?.('media-gallery')) return value;
    return value.closest?.('media-gallery') || value.querySelector?.('media-gallery') || null;
  };

  const isMainProductGallery = (mediaGallery) => Boolean(
    mediaGallery?.closest?.('product-info[id^="MainProduct-"]')
  );

  const mergeOptions = (base = {}, next = {}) => ({
    ...base,
    ...next,
    contentChanged: Boolean(base.contentChanged || next.contentChanged),
    imagesChanged: Boolean(base.imagesChanged || next.imagesChanged),
    resetPosition: next.resetPosition !== undefined ? next.resetPosition : base.resetPosition,
    prepend: next.prepend !== undefined ? next.prepend : base.prepend,
    defer: Boolean(base.defer || next.defer),
  });

  class ProductGalleryController {
    constructor(mediaGallery) {
      this.mediaGallery = mediaGallery;
      this.originalParent = mediaGallery.closest('.product__media-wrapper');
      this.viewer = null;
      this.list = null;
      this.thumbnailViewer = null;
      this.thumbnailList = null;
      this.progressBar = null;
      this.slideCounter = null;
      this.mode = '';
      this.flkty = null;
      this.connected = false;
      this.contentUpdateDepth = 0;
      this.pendingOptions = {};
      this.pendingFrame = 0;
      this.pendingSecondFrame = 0;
      this.nativeScrollFrame = 0;
      this.activeMediaId = '';
      this.boundList = null;
      this.slideOrder = [];
      this.orderList = null;
      this.flickityEpoch = 0;

      this.onNativeScroll = this.onNativeScroll.bind(this);
      this.onListImageLoad = this.onListImageLoad.bind(this);
      this.refreshRefs();
    }

    refreshRefs() {
      const previousList = this.list;
      this.viewer = this.mediaGallery.querySelector('[id^="GalleryViewer"]');
      this.list = this.mediaGallery.querySelector('[id^="Slider-Gallery"], .product__media-list');
      this.thumbnailViewer = this.mediaGallery.querySelector('[id^="GalleryThumbnails"]');
      this.thumbnailList = this.thumbnailViewer?.querySelector('ul') || null;
      this.progressBar = this.mediaGallery.querySelector('#progressBar, .progress-bar');
      this.slideCounter = this.mediaGallery.querySelector('#slideCounter, .slide-counter');

      if (previousList !== this.list) {
        this.unbindList(previousList);
        this.slideOrder = [];
        this.orderList = this.list;
        if (this.list) this.adoptCurrentSlideOrder();
        if (this.connected) this.bindList(this.list);
      } else if (this.list && (!this.slideOrder.length || this.orderList !== this.list)) {
        this.orderList = this.list;
        this.adoptCurrentSlideOrder();
      }
    }

    bindList(list) {
      if (!list || this.boundList === list) return;
      this.boundList = list;
      list.addEventListener('scroll', this.onNativeScroll, { passive: true });
      list.addEventListener('load', this.onListImageLoad, true);
    }

    unbindList(list = this.boundList) {
      if (!list) return;
      list.removeEventListener('scroll', this.onNativeScroll);
      list.removeEventListener('load', this.onListImageLoad, true);
      if (this.boundList === list) this.boundList = null;
    }

    connect() {
      if (this.connected) return this;
      this.connected = true;
      this.mediaGallery.dataset.productGalleryController = 'ready';
      this.refreshRefs();
      this.bindList(this.list);
      this.ensureStableParent();
      this.refresh({ reason: 'connect', resetPosition: false });
      return this;
    }

    destroy() {
      this.cancelScheduledRefresh();
      if (this.nativeScrollFrame) cancelAnimationFrame(this.nativeScrollFrame);
      this.nativeScrollFrame = 0;
      this.destroyFlickity();
      this.unbindList();
      this.viewer?.removeAttribute('data-product-gallery-mode');
      this.thumbnailViewer?.removeAttribute('data-product-gallery-mode');
      this.mediaGallery.removeAttribute('data-product-gallery-mode');
      delete this.mediaGallery.dataset.productGalleryController;
      this.connected = false;
      controllers.delete(this);
      registry.delete(this.mediaGallery);
      if (this.mediaGallery.__brProductGalleryController === this) {
        delete this.mediaGallery.__brProductGalleryController;
      }
    }

    ensureStableParent() {
      if (!this.originalParent?.isConnected) {
        this.originalParent = this.mediaGallery.closest('.product__media-wrapper') || this.originalParent;
      }
      if (
        this.originalParent?.isConnected
        && this.mediaGallery.isConnected
        && !this.originalParent.contains(this.mediaGallery)
      ) {
        this.originalParent.appendChild(this.mediaGallery);
      }
    }

    desiredMode() {
      return this.mediaGallery.dataset.desktopLayout === 'carousel_peek'
        && window.innerWidth >= DESKTOP_FLICKITY_MIN
        && typeof window.Flickity === 'function'
        ? 'flickity'
        : 'native';
    }

    setViewerMode(mode) {
      this.mode = mode;
      if (this.viewer) this.viewer.dataset.productGalleryMode = mode;
      if (this.thumbnailViewer) {
        this.thumbnailViewer.dataset.productGalleryMode = mode === 'updating' ? 'updating' : 'native';
      }
      this.mediaGallery.dataset.productGalleryMode = mode;
    }

    allSlides() {
      if (!this.list) return [];
      return Array.from(this.list.querySelectorAll('.product__media-item'));
    }

    adoptCurrentSlideOrder() {
      if (!this.list) {
        this.slideOrder = [];
        this.orderList = null;
        return;
      }
      this.orderList = this.list;
      this.slideOrder = this.allSlides();
    }

    reconcileSlideOrder() {
      if (!this.list) return [];
      const current = this.allSlides();
      const currentSet = new Set(current);
      const retained = (this.orderList === this.list ? this.slideOrder : [])
        .filter((slide) => currentSet.has(slide));
      const retainedSet = new Set(retained);
      current.forEach((slide) => {
        if (!retainedSet.has(slide)) {
          retained.push(slide);
          retainedSet.add(slide);
        }
      });
      this.orderList = this.list;
      this.slideOrder = retained;
      return retained;
    }

    restoreSlideOrder() {
      if (!this.list) return;
      this.reconcileSlideOrder().forEach((slide) => {
        if (slide.parentElement !== this.list || this.list.lastElementChild !== slide) {
          this.list.appendChild(slide);
        }
      });
    }

    isContextSlide(slide) {
      return Boolean(slide?.matches?.(
        '[data-bandware-context-slot], [data-b1-link-context-slot], [data-b1-frame-context-slot]'
      ));
    }

    isContextThumbnail(item) {
      return Boolean(item?.matches?.(
        '[data-bandware-context-thumbnail], [data-b1-link-context-thumbnail], [data-b1-frame-context-thumbnail]'
      ));
    }

    flickitySlides(instance = this.flkty) {
      if (!instance?.cells) return [];
      return instance.cells
        .map((cell) => cell?.element)
        .filter((slide) => slide && !slide.hidden && slide.style.display !== 'none');
    }

    slidesForCurrentMode() {
      if (this.mode === 'flickity' && this.flkty) {
        const slides = this.flickitySlides(this.flkty);
        if (slides.length) return slides;
      }
      return this.visibleSlides();
    }

    visibleSlides() {
      // `hidden` is the gallery-membership source of truth. Flickity itself writes
      // aria-hidden="true" onto every non-selected cell, so aria-hidden must never
      // be interpreted as an excluded gallery item or desktop totals collapse to 1.
      return this.reconcileSlideOrder().filter((slide) => (
        !slide.hidden
        && slide.style.display !== 'none'
      ));
    }

    findSlide(mediaId) {
      if (!mediaId) return null;
      return this.allSlides().find((slide) => slide.dataset.mediaId === String(mediaId)) || null;
    }

    findThumbnail(mediaId) {
      if (!mediaId || !this.thumbnailViewer) return null;
      return Array.from(this.thumbnailViewer.querySelectorAll('[data-target]'))
        .find((thumbnail) => thumbnail.dataset.target === String(mediaId)) || null;
    }

    currentActiveSlide() {
      const visible = this.visibleSlides();
      return visible.find((slide) => slide.classList.contains('is-active')) || visible[0] || null;
    }

    currentActiveMediaId() {
      return this.activeMediaId || this.currentActiveSlide()?.dataset.mediaId || '';
    }

    activeIndex(slides = this.visibleSlides()) {
      if (!slides.length) return 0;
      const mediaId = this.currentActiveMediaId();
      const byId = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (byId >= 0) return byId;
      const byClass = slides.findIndex((slide) => slide.classList.contains('is-active'));
      return Math.max(0, byClass);
    }

    markActive(activeSlide) {
      if (!activeSlide) return null;
      this.allSlides().forEach((slide) => slide.classList.toggle('is-active', slide === activeSlide));
      this.activeMediaId = activeSlide.dataset.mediaId || '';
      return activeSlide;
    }

    prependMedia(activeSlide) {
      if (!activeSlide || !this.list) return;

      // beginContentUpdate() has already flattened any Flickity DOM before an
      // intentional reorder. Keep the live Shopify order established inside the
      // transaction, then move only the selected item within its permitted subset.

      if (this.isContextSlide(activeSlide)) {
        if (this.list.firstElementChild !== activeSlide) this.list.prepend(activeSlide);
      } else {
        // Contextual B¹ slots are required to remain before all native product
        // media. A normal variant featured image may lead the native subset, but
        // it must never jump ahead of the contextual gallery slots.
        const contextSlides = this.reconcileSlideOrder().filter((slide) => this.isContextSlide(slide));
        const lastContext = contextSlides[contextSlides.length - 1] || null;
        if (lastContext) {
          if (lastContext.nextElementSibling !== activeSlide) lastContext.after(activeSlide);
        } else if (this.list.firstElementChild !== activeSlide) {
          this.list.prepend(activeSlide);
        }
      }

      const thumbnail = this.findThumbnail(activeSlide.dataset.mediaId);
      const thumbnailItem = thumbnail?.closest?.('li') || thumbnail;
      const thumbnailParent = thumbnailItem?.parentElement || null;
      if (thumbnailParent) {
        if (this.isContextThumbnail(thumbnailItem)) {
          if (thumbnailParent.firstElementChild !== thumbnailItem) thumbnailParent.prepend(thumbnailItem);
        } else {
          const contextThumbnails = Array.from(thumbnailParent.children).filter((item) => this.isContextThumbnail(item));
          const lastContextThumbnail = contextThumbnails[contextThumbnails.length - 1] || null;
          if (lastContextThumbnail) {
            if (lastContextThumbnail.nextElementSibling !== thumbnailItem) lastContextThumbnail.after(thumbnailItem);
          } else if (thumbnailParent.firstElementChild !== thumbnailItem) {
            thumbnailParent.prepend(thumbnailItem);
          }
        }
      }

      // This is an explicit order change, so it becomes the next canonical order.
      this.adoptCurrentSlideOrder();
    }

    setActiveMedia(mediaId, options = {}) {
      const autoTransaction = this.contentUpdateDepth === 0 && Boolean(options.prepend);
      if (autoTransaction) this.beginContentUpdate({ reason: options.reason || 'active-media-prepend' });

      this.refreshRefs();
      let activeSlide = this.findSlide(mediaId);
      if (!activeSlide || activeSlide.hidden || activeSlide.style.display === 'none') {
        activeSlide = this.visibleSlides()[0] || activeSlide;
      }
      if (!activeSlide) {
        if (autoTransaction) this.endContentUpdate({ reason: options.reason || 'active-media-prepend' });
        return null;
      }

      if (options.prepend) this.prependMedia(activeSlide);
      this.markActive(activeSlide);

      if (this.contentUpdateDepth === 0) {
        const activeIndex = this.selectActiveInCurrentMode({ resetPosition: options.resetPosition !== false });
        this.updateProgress(activeIndex);
      }

      if (autoTransaction) {
        this.endContentUpdate({
          reason: options.reason || 'active-media-prepend',
          activeMediaId: this.activeMediaId,
          resetPosition: options.resetPosition !== false,
          contentChanged: true,
        });
      }
      return activeSlide;
    }

    beginContentUpdate(options = {}) {
      const interruptedDeferredRefresh = Boolean(this.pendingFrame || this.pendingSecondFrame);
      if (this.contentUpdateDepth === 0 && interruptedDeferredRefresh) {
        this.cancelScheduledRefresh();
        // A product-family projection supersedes the generic deferred ProductInfo
        // pass completely. Do not inherit its active ID, prepend instruction or
        // deferred timing into the final contextual gallery state.
        this.pendingOptions = mergeOptions({}, { ...options, defer: false });
      } else {
        this.pendingOptions = mergeOptions(this.pendingOptions, options);
      }

      if (this.contentUpdateDepth === 0) {
        this.cancelScheduledRefresh();
        this.refreshRefs();
        this.ensureStableParent();
        this.activeMediaId = this.currentActiveMediaId();
        this.setViewerMode('updating');
        this.destroyFlickity();
      }
      this.contentUpdateDepth += 1;
    }

    endContentUpdate(options = {}) {
      this.pendingOptions = mergeOptions(this.pendingOptions, {
        contentChanged: true,
        ...options,
      });
      this.contentUpdateDepth = Math.max(0, this.contentUpdateDepth - 1);
      if (this.contentUpdateDepth > 0) return;

      if (this.pendingOptions.defer) {
        const deferredOptions = this.pendingOptions;
        this.pendingOptions = {};
        this.scheduleRefresh(deferredOptions, 2);
        return;
      }

      const finalOptions = this.pendingOptions;
      this.pendingOptions = {};
      this.refresh(finalOptions);
    }

    cancelScheduledRefresh() {
      if (this.pendingFrame) cancelAnimationFrame(this.pendingFrame);
      if (this.pendingSecondFrame) cancelAnimationFrame(this.pendingSecondFrame);
      this.pendingFrame = 0;
      this.pendingSecondFrame = 0;
    }

    scheduleRefresh(options = {}, frames = 1) {
      this.pendingOptions = mergeOptions(this.pendingOptions, options);
      if (this.pendingFrame || this.pendingSecondFrame) return;

      this.pendingFrame = requestAnimationFrame(() => {
        this.pendingFrame = 0;
        if (frames > 1) {
          this.pendingSecondFrame = requestAnimationFrame(() => {
            this.pendingSecondFrame = 0;
            const finalOptions = this.pendingOptions;
            this.pendingOptions = {};
            this.refresh(finalOptions);
          });
          return;
        }
        const finalOptions = this.pendingOptions;
        this.pendingOptions = {};
        this.refresh(finalOptions);
      });
    }

    refresh(options = {}) {
      if (this.contentUpdateDepth > 0) {
        this.pendingOptions = mergeOptions(this.pendingOptions, options);
        return;
      }

      this.cancelScheduledRefresh();
      this.refreshRefs();
      this.ensureStableParent();
      if (!this.list) return;

      const activeMediaId = options.activeMediaId || this.currentActiveMediaId();
      if (options.contentChanged && this.flkty) this.destroyFlickity();
      if (options.contentChanged) this.setViewerMode('updating');

      let activeSlide = this.findSlide(activeMediaId);
      if (!activeSlide || activeSlide.hidden || activeSlide.style.display === 'none') {
        activeSlide = this.visibleSlides()[0] || activeSlide;
      }
      if (activeSlide && options.prepend) this.prependMedia(activeSlide);
      if (activeSlide) this.markActive(activeSlide);

      const nextMode = this.desiredMode();
      if (nextMode === 'flickity') {
        if (this.mode !== 'flickity' || !this.flkty || options.contentChanged) {
          this.initFlickity();
        } else {
          this.setViewerMode('flickity');
          this.flkty.resize();
        }
      } else {
        this.destroyFlickity();
        this.setViewerMode('native');
        this.resetNativePages();
      }

      const activeIndex = this.selectActiveInCurrentMode({ resetPosition: options.resetPosition !== false });
      this.updateProgress(activeIndex);

      this.mediaGallery.dispatchEvent(new CustomEvent('brilinton:product-gallery:updated', {
        bubbles: true,
        detail: {
          reason: options.reason || 'refresh',
          mode: this.mode,
          activeMediaId: this.currentActiveMediaId(),
          visibleCount: this.visibleSlides().length,
          contentChanged: Boolean(options.contentChanged),
        },
      }));
    }

    resetThumbnailPages() {
      if (!this.thumbnailViewer) return;
      this.thumbnailViewer.sliderItems = this.thumbnailViewer.querySelectorAll('[id^="Slide-"]');
      this.thumbnailViewer.resetPages?.();
    }

    resetNativePages() {
      if (this.viewer) {
        this.viewer.sliderItems = this.viewer.querySelectorAll('[id^="Slide-"]');
        this.viewer.resetPages?.();
      }
      this.resetThumbnailPages();
    }

    selectActiveInCurrentMode({ resetPosition = true } = {}) {
      const slides = this.slidesForCurrentMode();
      if (!slides.length) return 0;
      const index = this.activeIndex(slides);
      const activeSlide = slides[index] || slides[0];
      this.markActive(activeSlide);

      if (this.mode === 'flickity' && this.flkty) {
        if (this.flkty.selectedIndex !== index) this.flkty.select(index, false, true);
        return index;
      }

      if (resetPosition && this.list?.scrollTo && (window.innerWidth < 750 || this.thumbnailViewer)) {
        this.list.scrollTo({ left: activeSlide.offsetLeft, behavior: 'auto' });
      }
      return index;
    }

    initFlickity() {
      this.destroyFlickity();
      this.refreshRefs();
      this.restoreSlideOrder();
      if (!this.list || typeof window.Flickity !== 'function') {
        this.setViewerMode('native');
        this.resetNativePages();
        return;
      }

      const visible = this.visibleSlides();
      if (!visible.length) {
        this.setViewerMode('native');
        this.resetNativePages();
        return;
      }

      const initialIndex = this.activeIndex(visible);
      const epoch = ++this.flickityEpoch;

      this.setViewerMode('flickity');
      this.resetThumbnailPages();
      const flkty = new window.Flickity(this.list, {
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: visible.length > 1,
        wrapAround: false,
        adaptiveHeight: false,
        imagesLoaded: true,
        percentPosition: true,
        draggable: visible.length > 1,
        initialIndex,
        cellSelector: '.product__media-item:not([hidden])',
      });
      this.flkty = flkty;

      flkty.on('change', (index) => {
        if (this.flkty !== flkty || this.flickityEpoch !== epoch) return;
        const currentSlides = this.flickitySlides(flkty);
        const activeSlide = currentSlides[index] || currentSlides[0];
        if (activeSlide) this.markActive(activeSlide);
        this.updateProgress(index);
        if (activeSlide && this.viewer) {
          this.viewer.dispatchEvent(new CustomEvent('slideChanged', {
            detail: {
              currentPage: index + 1,
              currentElement: activeSlide,
            },
          }));
        }
      });

      flkty.on('settle', () => {
        if (this.flkty !== flkty || this.flickityEpoch !== epoch) return;
        flkty.resize();
        this.updateProgress(flkty.selectedIndex || 0);
      });

      // `initialIndex` prevents Flickity from painting cell 0 and then jumping to
      // a later active cell after initialization. The active class and counter are
      // synchronized from the actual Flickity cell order immediately.
      const currentSlides = this.flickitySlides(flkty);
      const selectedIndex = Math.max(0, Math.min(
        Math.max(currentSlides.length - 1, 0),
        Number.isInteger(flkty.selectedIndex) ? flkty.selectedIndex : initialIndex
      ));
      const activeSlide = currentSlides[selectedIndex] || currentSlides[0];
      if (activeSlide) this.markActive(activeSlide);
      flkty.resize();
      this.updateProgress(selectedIndex);

      requestAnimationFrame(() => {
        if (this.flkty !== flkty || this.flickityEpoch !== epoch) return;
        flkty.resize();
        this.updateProgress(flkty.selectedIndex || 0);
      });
    }

    destroyFlickity() {
      const active = this.flkty
        || (this.list && typeof window.Flickity?.data === 'function' ? window.Flickity.data(this.list) : null);

      // Invalidate old asynchronous change/settle callbacks before destroy() can
      // emit anything. A stale Flickity instance must never select a slide in the
      // newly projected gallery.
      this.flickityEpoch += 1;
      this.flkty = null;

      if (active) active.destroy();

      // Flickity only moves selector-matching cells into its slider. Hidden B¹
      // context slots remain outside. On destroy, its cells are appended back and
      // can otherwise drift behind those hidden slots. Restore the Liquid-defined
      // canonical order after every teardown.
      this.restoreSlideOrder();
    }

    onNativeScroll() {
      if (this.mode !== 'native' || this.nativeScrollFrame) return;
      this.nativeScrollFrame = requestAnimationFrame(() => {
        this.nativeScrollFrame = 0;
        this.updateProgress();
      });
    }

    onListImageLoad(event) {
      if (event.target?.tagName !== 'IMG') return;
      if (this.mode === 'flickity' && this.flkty) {
        this.scheduleRefresh({ reason: 'image-load', imagesChanged: true, resetPosition: false });
      }
    }

    nativeIndex(slides) {
      if (!slides.length || !this.list) return 0;
      const left = this.list.scrollLeft || 0;
      let bestIndex = 0;
      let bestDistance = Number.POSITIVE_INFINITY;
      slides.forEach((slide, index) => {
        const distance = Math.abs((slide.offsetLeft || 0) - left);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });
      return bestIndex;
    }

    updateProgress(index) {
      const slides = this.slidesForCurrentMode();
      const total = Math.max(1, slides.length);
      let safeIndex = Number.isInteger(index)
        ? index
        : this.mode === 'flickity' && this.flkty
          ? this.flkty.selectedIndex || 0
          : this.nativeIndex(slides);
      safeIndex = Math.max(0, Math.min(total - 1, safeIndex));

      if (slides[safeIndex]) this.activeMediaId = slides[safeIndex].dataset.mediaId || this.activeMediaId;

      if (this.viewer) {
        this.viewer.querySelectorAll('.slider-counter--current').forEach((node) => {
          node.textContent = String(safeIndex + 1);
        });
        this.viewer.querySelectorAll('.slider-counter--total').forEach((node) => {
          node.textContent = String(total);
        });
      }

      if (this.slideCounter) this.slideCounter.textContent = `${safeIndex + 1} / ${total}`;

      if (this.progressBar) {
        let percentage = ((safeIndex + 1) / total) * 100;
        if (
          this.mode === 'native'
          && this.mediaGallery.dataset.desktopLayout === 'carousel_peek'
          && window.innerWidth <= MOBILE_LAYOUT_MAX
          && this.list
          && total > 1
        ) {
          const maxScroll = Math.max(1, this.list.scrollWidth - this.list.clientWidth);
          const progress = Math.max(0, Math.min(1, this.list.scrollLeft / maxScroll));
          percentage = ((1 / total) + progress * ((total - 1) / total)) * 100;
        }
        this.progressBar.style.width = `${Math.max(0, Math.min(100, percentage))}%`;
      }
    }
  }

  const getOrCreate = (value, connect = true) => {
    const mediaGallery = asGallery(value);
    if (!mediaGallery) return null;
    let controller = registry.get(mediaGallery);
    // A registered main-product gallery may be transiently moved outside its
    // ProductInfo wrapper by another runtime. Retain that controller so it can
    // heal the approved Liquid-rendered parent instead of losing ownership.
    if (!controller && !isMainProductGallery(mediaGallery)) return null;
    if (!controller) {
      controller = new ProductGalleryController(mediaGallery);
      registry.set(mediaGallery, controller);
      controllers.add(controller);
      mediaGallery.__brProductGalleryController = controller;
    }
    if (connect && !controller.connected && controller.contentUpdateDepth === 0) controller.connect();
    return controller;
  };

  const api = {
    version: 'R44-265',
    ensure(value) {
      return getOrCreate(value, true);
    },
    get(value) {
      const mediaGallery = asGallery(value);
      return mediaGallery ? registry.get(mediaGallery) || null : null;
    },
    beginContentUpdate(value, options = {}) {
      const controller = getOrCreate(value, false);
      if (!controller) return false;
      controller.beginContentUpdate(options);
      return true;
    },
    endContentUpdate(value, options = {}) {
      const controller = getOrCreate(value, false);
      if (!controller) return false;
      controller.endContentUpdate(options);
      return true;
    },
    setActiveMedia(value, mediaId, options = {}) {
      const controller = getOrCreate(value, false);
      if (!controller) return null;
      if (!controller.connected && controller.contentUpdateDepth === 0) controller.connect();
      return controller.setActiveMedia(mediaId, options);
    },
    refresh(value, options = {}) {
      const controller = getOrCreate(value, true);
      controller?.refresh(options);
      return controller;
    },
    requestRefresh(value, options = {}) {
      const controller = getOrCreate(value, true);
      controller?.scheduleRefresh(options);
      return controller;
    },
    destroy(value) {
      const mediaGallery = asGallery(value);
      const controller = mediaGallery ? registry.get(mediaGallery) : null;
      controller?.destroy();
    },
    boot(scope = document) {
      const galleries = [];
      if (scope?.matches?.('media-gallery') && isMainProductGallery(scope)) galleries.push(scope);
      scope?.querySelectorAll?.('product-info[id^="MainProduct-"] media-gallery').forEach((gallery) => galleries.push(gallery));
      galleries.forEach((gallery) => getOrCreate(gallery, true));
    },
  };

  window.BrilintonProductGallery = api;

  const refreshAll = () => {
    controllers.forEach((controller) => {
      if (!controller.mediaGallery.isConnected) {
        controller.destroy();
        return;
      }
      controller.refresh({ reason: 'viewport-change', resetPosition: false });
    });
    api.boot(document);
  };

  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(refreshAll, RESIZE_DEBOUNCE_MS);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => api.boot(document), { once: true });
  } else {
    api.boot(document);
  }

  window.addEventListener('load', refreshAll, { once: true });
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('brilinton:layout-refresh', refreshAll);
  document.addEventListener('product-info:loaded', (event) => api.boot(event.target || document));
  document.addEventListener('shopify:section:load', (event) => api.boot(event.target || document));
  document.addEventListener('shopify:section:unload', (event) => {
    const galleries = [];
    if (event.target?.matches?.('media-gallery')) galleries.push(event.target);
    event.target?.querySelectorAll?.('media-gallery').forEach((gallery) => galleries.push(gallery));
    galleries.forEach((gallery) => api.destroy(gallery));
  });
})();
